import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

type RevealContainer = HTMLElement & { _revealTl?: gsap.core.Timeline };

/**
 * Clip-path reveal animation on scroll.
 * Children start fully clipped (hidden) and reveal sequentially when
 * the container enters the viewport, with a staggered delay.
 */
export function initClipPathReveal(
    containerSelector: string,
    childSelector: string,
    maxChildren = 3
): void {
    const containers = gsap.utils.toArray<HTMLElement>(containerSelector);
    if (!containers.length) return;

    const clipHidden = 'polygon(0px 100%, 100% 100%, 100% 100%, 0% 100%)';
    const clipVisible = 'polygon(0px 0%, 100% 0%, 100% 100%, 0% 100%)';

    // Set initial hidden state
    gsap.set(`${containerSelector} > ${childSelector}`, { clipPath: clipHidden });

    // Build a paused timeline for each container
    containers.forEach((container) => {
        const tl = gsap.timeline({ paused: true });

        for (let i = 1; i <= maxChildren; i++) {
            const child = container.querySelector(`${childSelector}:nth-of-type(${i})`);
            if (child) {
                tl.to(child, { clipPath: clipVisible });
            }
        }

        (container as RevealContainer)._revealTl = tl;
    });

    // Trigger timelines in batches when they enter the viewport
    ScrollTrigger.batch(containers, {
        onEnter: (batch) => {
            batch.forEach((target, i) => {
                gsap.delayedCall(i * 0.3, () => {
                    (target as RevealContainer)._revealTl?.play();
                });
            });
        },
        once: true,
    });
}
